import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from 'mobx-react-lite';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useService } from '~/services/service.base';
import { UIService } from '~/services/service.ui';
import { Breadcrumb } from '~/views/molecules/nav.breadcrumbs';
import { PageWrapper } from '~/views/organisms/wrapper.page';
import { useTranslation } from 'react-i18next';
import { MediaSearchService } from '~/services/service.media.search';
import { EBreadcrumbKey, EFilterType } from '~/shared/types';
import { useStyles } from '~/pages/funds/search-result/styles';
import { PageHeader } from '~/views/molecules/page.header';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { Icons } from '~/views/atoms/icons';
import { Column } from '~/views/atoms/boxes';
import { TabsFilter } from '~/views/molecules/tabs.filter';
import { Filter } from '~/views/organisms/filter/filter';
import { ListImageTableView } from '~/views/organisms/list-image-table-view/list-image-table-view';
import { runInAction } from 'mobx';
import { useSearchForm } from '~/shared/hooks/useSearchForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { ImagesList } from '~/views/molecules/list-image/list.image';
import { CircleCounter } from '~/views/atoms/circle-counter';
import { ROUTES } from '~/routes/routes';
import { ServiceCategoryTag } from '~/services/service.category.tag';
import { AuthService } from '~/services/service.auth';
import { MediaSwiper } from '~/views/organisms/media-swiper/media.swiper';
import { ServiceMediaViewer } from '~/services/service.media.viewer';
import { getBreadcrumbs } from '~/tools/breadcrumb';
import { useFileDownload, useFileInfo } from '~/shared/hooks';
export const PageFundsSearchResult = observer(() => {
    const { t } = useTranslation();
    const { breadcrumbsSvc } = useService(UIService);
    const mediasService = useService(MediaSearchService);
    const { openFilterSvc, filterSvc, openDialogSvc } = mediasService;
    const location = useLocation();
    const { searchParams, query } = useMemo(() => location.state || {}, [location.state]);
    const navigate = useNavigate();
    const serviceCategoryTag = useService(ServiceCategoryTag);
    const defaultBreadcrumbs = useMemo(() => [
        {
            key: EBreadcrumbKey.recent,
        },
    ], []);
    const isCategoryContent = useMemo(() => location.pathname === ROUTES.FUNDS.MEDIA.CATEGORY_CONTENT.path, [location.pathname]);
    useEffect(() => {
        if (isCategoryContent) {
            breadcrumbsSvc.use(getBreadcrumbs(defaultBreadcrumbs, mediasService));
        }
    }, [breadcrumbsSvc, defaultBreadcrumbs, mediasService, isCategoryContent]);
    filterSvc.use([
        { value: EFilterType.all, label: t('MEDIAS_TABFILTER_ALL') },
        { value: EFilterType.media, label: t('MEDIAS_TABFILTER_MEDIAS') },
        { value: EFilterType.photo, label: t('MEDIAS_TABFILTER_PHOTOS') },
        { value: EFilterType.video, label: t('MEDIAS_TABFILTER_VIDEOS') },
        { value: EFilterType.documents, label: t('MEDIAS_TABFILTER_DOCUMENTS') },
        { value: EFilterType.archives, label: t('MEDIAS_TABFILTER_ARTICLES') },
    ]);
    const classes = useStyles();
    const searchForm = useSearchForm();
    const isImageView = useMemo(() => {
        return (mediasService.filterType &&
            [EFilterType.media, EFilterType.photo, EFilterType.video].includes(mediasService.filterType));
    }, [mediasService.filterType]);
    const selectedTagsCount = useMemo(() => {
        const tagsList = mediasService.tags[mediasService.categoryId]?.split(',').filter(tag => !!tag);
        return tagsList?.length || 0;
    }, [mediasService.tags, mediasService.categoryId]);
    const resetFiltersHandler = useCallback(() => {
        mediasService.setOriginalFilters({});
        mediasService.setTags([]);
        openFilterSvc.handleClose();
    }, [mediasService, openFilterSvc]);
    const resetSearchField = useCallback(() => {
        mediasService.searchFieldSvc.handleClear();
        mediasService.setQuery(undefined);
        mediasService.getMediaItems();
        navigate(ROUTES.FUNDS.path);
    }, [mediasService, navigate]);
    const authService = useService(AuthService);
    const { fileInfoHandler, fileInfoContent } = useFileInfo();
    const { downloadFromServer, downloadImageHandler } = useFileDownload();
    const serviceMediaViewer = useService(ServiceMediaViewer);
    const closeImageHandler = useCallback(() => {
        serviceMediaViewer.mediaViewOpenService.handleClose();
    }, [serviceMediaViewer.mediaViewOpenService]);
    const clickImageHandler = useCallback((item) => {
        serviceMediaViewer.setOpenedItem(item);
        serviceMediaViewer.setOpenedItemIndex(serviceMediaViewer.filterMediaItems(mediasService.itemsData || []));
        serviceMediaViewer.mediaViewOpenService.handleOpen();
    }, [mediasService.itemsData, serviceMediaViewer]);
    const getPageTitle = useCallback(() => {
        if (mediasService.subCategories.length > 0 && !mediasService.searchFieldSvc.value) {
            return mediasService.subCategories[mediasService.lastSubCategoryIndex].breadCrumbName;
        }
        else {
            return t('SEARCH_RESULT_PAGE_TITLE');
        }
    }, [mediasService.lastSubCategoryIndex, mediasService.searchFieldSvc.value, mediasService.subCategories, t]);
    return (_jsxs(PageWrapper, { children: [_jsx(Breadcrumb.Nav, { children: breadcrumbsSvc.children }), _jsx(PageHeader, { justifyContent: "space-between", children: _jsxs(Grid, { container: true, alignItems: "center", children: [_jsx(Grid, { xs: 6, md: 9, item: true, container: true, flexDirection: "column", children: _jsx(Typography, { sx: { wordBreak: 'break-word' }, variant: "h2", children: getPageTitle() }) }), _jsxs(Grid, { xs: 6, md: 3, item: true, container: true, flexDirection: "row", justifyContent: "flex-end", children: [_jsxs(Box, { position: "relative", children: [serviceCategoryTag.tags?.length > 0 ? (_jsx(IconButton, { color: "inherit", onClick: openFilterSvc.handleOpen, children: _jsx(Icons.Filter, {}) })) : null, selectedTagsCount > 0 ? (_jsxs(Fragment, { children: [_jsx(IconButton, { color: "inherit", edge: "end", onClick: resetFiltersHandler, children: _jsx(Icons.Close, { color: "primary" }) }), _jsx(CircleCounter, { className: classes.circleCounter, counter: selectedTagsCount })] })) : null] }), _jsxs(Box, { className: classes.filterIcon, children: [_jsx(IconButton, { color: "inherit", onClick: openDialogSvc.handleOpen, children: _jsx(Icons.Search, {}) }), query || searchParams ? (_jsx(IconButton, { color: "inherit", edge: "end", onClick: resetSearchField, children: _jsx(Icons.Close, { color: "primary" }) })) : null] })] })] }) }), _jsxs(Column, { py: 1.5, gap: 2, children: [_jsx(TabsFilter, { items: filterSvc.tabs, className: classes.container, value: filterSvc.tab, onChange: (value) => {
                            mediasService.setFilterType(value);
                            mediasService.filterSvc.handleChange(value);
                            runInAction(() => {
                                mediasService.resetItems();
                                mediasService.paginatorService.setOffset(undefined);
                                mediasService.paginatorService.resetPaginatorData();
                                mediasService.getMediaItems(undefined, searchParams || undefined, mediasService.tags);
                            });
                        } }), isImageView ? (_jsx(Box, { px: 2, children: _jsx(ImagesList, { offset: mediasService.lastItemId, clickImageHandler: clickImageHandler, itemData: mediasService.itemsData || [] }) })) : (_jsx(ListImageTableView, { itemData: mediasService.itemsData || [], clickImageHandler: clickImageHandler, clickByFileInfoHandler: fileInfoHandler, clickDocumentHandler: downloadImageHandler, offset: mediasService.lastItemId }))] }), openFilterSvc.open ? _jsx(Filter, {}) : null, searchForm, fileInfoContent, serviceMediaViewer.mediaViewOpenService.open && serviceMediaViewer.openedItem && (_jsx(MediaSwiper, { closeButtonHandler: closeImageHandler, items: serviceMediaViewer.filterMediaItems(mediasService.itemsData || []), initialSlideIndex: serviceMediaViewer.openedItemIndex, infoButtonHandler: fileInfoHandler, downloadButtonHandler: authService.isLogged ? downloadFromServer : undefined }))] }));
});
